import * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import React from 'react';
import { PiccoloError } from '../../../components/generic/PiccoloError';
import {
  getDocumentVariables,
  getParticipantDocumentTemplateExtensions,
} from '../../../components/tiptap/utils';
import { useParticipantQuery } from '../../../graphql/hooks/useParticipantQuery';
import { useTemplateQuery } from '../../../graphql/hooks/useTemplateQuery';
import { useAppContext } from '../../../hooks/useAppContext';
import { TemplateEditor } from '../TemplateEditor';

interface Props {
  participantId: string;
  templateId: string;
}

export const ParticipantDocumentTemplatePreview = (props: Props) => {
  const { participantId, templateId } = props;
  const { organization, user } = useAppContext();

  const templateQuery = useTemplateQuery({
    templateId,
    organizationId: organization.id,
  });

  const participantQuery = useParticipantQuery({
    participantId,
    organizationId: organization.id,
  });

  const template = templateQuery.data?.organization?.template as Common.Template | null;

  const participant = participantQuery.data?.organization?.participant as Common.Participant | null;

  const variables = React.useMemo(() => {
    if (!template || !participant) {
      return [];
    }

    return getDocumentVariables({
      user,
      organization,
      participant,
    });
  }, [template, participant, user, organization]);

  if (templateQuery.isLoading || participantQuery.isLoading) {
    return <Spin />;
  }

  if (templateQuery.error) {
    return <PiccoloError error={templateQuery.error} />;
  }

  if (participantQuery.error) {
    return <PiccoloError error={participantQuery.error} />;
  }

  if (!participant) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'ParticipantNotFound',
            message: 'Participant not found',
          })
        }
      />
    );
  }

  if (!template) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'TemplateNotFound',
            message: 'Template not found',
          })
        }
      />
    );
  }

  const extensions = getParticipantDocumentTemplateExtensions({ variables });

  return (
    <TemplateEditor
      key={template.id}
      title={() => (
        <ChakraV3.Heading size='3xl' fontWeight='bold' mb={8}>
          {template.name}
        </ChakraV3.Heading>
      )}
      documentId={`template/${template.id}`}
      extensions={extensions}
      isEditable={false}
    />
  );
};
